/* eslint-disable import/no-unresolved */
import localForage from 'localforage'; // https://localforage.docschina.org/
import axios from 'axios';
import {
  getHashString, isNullOrUndefined,
} from './utils';

const Fetch = class {
  constructor($config) {
    this.axios = axios;
    this.$config = $config;
    this.ERROR_CODE = {
      SUCCESS: '',
      BIZ_ERROR: 'BIZ_ERROR',
      TIMEOUT: 'TIMEOUT',
      NETWORK_ERROR: 'NETWORK_ERROR',
      NO_RESPONSE: 'NO_RESPONSE',
      NO_RESPONSE_DATA: 'NO_RESPONSE_DATA',
      SERVER_ERROR: 'SERVER_ERROR',
    };
    this.HTTP_METHOD = {
      GET: 'GET',
      POST: 'POST',
    };
    this.SOURCE = {
      MOCK: 'MOCK',
      CACHE: 'CACHE',
      AJAX: 'AJAX',
      CUSTOM: 'CUSTOM',
      BRIDGE: 'BRIDGE',
      IDEM: 'IDEM',
    };
    // 钩子
    this.hooks = {
      beforeRequest() {},
      afterResponse() {},
    };
    // 内存缓存
    this.memoryCache = {};
    // mock配置数据（默认null不启用）
    this.mockConfig = null;
    // 进行中的可幂等请求
    this.idemOptions = {};
    // 请求自增ID
    this.increaseId = 0;

    // 配置baseURL
    axios.defaults.baseURL = this.$config.fetch.baseUrl;
    axios.defaults.timeout = this.$config.fetch.defaultTimeout ?? 1e4;
    axios.defaults.withCredentials = this.$config.fetch.withCredentials;

    // 初始化LocalForage
    if (this.$config.fetch.maxCacheCount > 0) {
      localForage.config({
        name: 'LocalForage',
        storeName: this.$config.fetch.cacheStoreName,
        version: 1.0,
      });
    }
  }

  /**
   * 执行单一网络请求，并执行缓存逻辑
   * @param {请求配置} options {
   *  url：接口地址（不需要base部分）
   *  headers: header对象
   *  params: 入参对象
   *  timeout: 超时毫秒数
   *  method: 请求方法（默认GET）
   *  idem: 启用幂等逻辑
   *  withCredentials: 跨域请求时是否需要使用凭证
   *  rawResponse: 是否响应内容"原汁原味"返回
   *  noTimestamp: get请求不自动添加时间戳参数
   *  axiosOptions: 直接传给axios的选项
   *  cache: {
   *    key: 接口缓存的key，如果不指定则根据接口名、入参自动生成
   *    online: 热缓存时长（ms）
   *    offline：冷缓存时长（ms）
   *  }
   * }
   */
  request(options) {
    // 记录起始时间和ID
    options.startTime = new Date().getTime();
    this.increaseId += 1;
    options.id = this.increaseId;

    // 请求前钩子
    const customResponseData = this.hooks.beforeRequest(options);
    if (customResponseData) {
      return this.resolveFetchResponse(
        options,
        customResponseData,
        200,
        '',
        this.ERROR_CODE.SUCCESS,
        '',
        this.SOURCE.CUSTOM,
        false,
        null,
      );
    }

    // Mock
    if (this.$config.fetch.mock && this.mockConfig) {
      const mockResponse = this.mockHandle(options);
      if (mockResponse) {
        return mockResponse;
      }
    }

    // 幂等逻辑
    if (options.method === this.HTTP_METHOD.GET && (options.idem ?? this.$config.fetch.idem)) {
      const optionsUniqueKey = this.getUniqueKey(options);
      for (const idemOptionId in this.idemOptions) {
        const idemOption = this.idemOptions[idemOptionId];
        if (idemOption.$idem && options.url === idemOption.url) {
          // 惰性计算已有请求hash值
          if (!idemOption.$idem.uniqueKey) {
            idemOption.$idem.uniqueKey = this.getUniqueKey(idemOption);
          }
          if (optionsUniqueKey === idemOption.$idem.uniqueKey) {
            // 命中幂等
            return new Promise(resolve => {
              idemOption.$idem.pendings.push({ options, resolve });
            });
          }
        }
      }

      options.$idem = { pendings: [] };
      this.idemOptions[options.id] = options;
    }

    const cacheConfig = options.cache;
    // 启用了缓存
    if (this.$config.fetch.maxCacheCount > 0 && cacheConfig) {
      // 生成缓存key
      cacheConfig.key = cacheConfig.key ?? `${options.url}-${this.getUniqueKey(options)}`;

      // 内存级缓存逻辑
      if (cacheConfig.online === null) {
        if (cacheConfig.key in this.memoryCache) {
          // 有内存级缓存，使用缓存
          return this.resolveFetchResponse(
            options,
            this.memoryCache[cacheConfig.key],
            200,
            '',
            this.ERROR_CODE.SUCCESS,
            '',
            this.SOURCE.CACHE,
            false,
            null,
          );
        }
        return this.ajaxWithRetry(options, null, false);
      }

      // 检查本地缓存是否存在
      return localForage.getItem(cacheConfig.key).then(cacheValue => {
        if (cacheValue && cacheValue.data) {
          // 有缓存，但需要检查是否过期
          if (cacheValue.time && (options.startTime - cacheValue.time) < cacheConfig.online) {
            // 缓存没过期，使用缓存
            return this.resolveFetchResponse(
              options,
              cacheValue.data,
              200,
              '',
              this.ERROR_CODE.SUCCESS,
              '',
              this.SOURCE.CACHE,
              false,
              null,
            );
          }
          // 缓存已过online过期时间(但不一定过了offline过期时间)，需先请求网络
          return this.ajaxWithRetry(options, cacheValue, false);
        }

        // 无效缓存，删除之
        localForage.removeItem(cacheConfig.key);
        return this.ajaxWithRetry(options, null, false);
      }).catch(() => this.ajaxWithRetry(options, null, false)); // 无缓存, 直接走ajax网络
    }

    // 该接口禁用了缓存，直接发起网络请求
    return this.ajaxWithRetry(options, null, false);
  }

  get(options) {
    return this.request({ ...options, method: this.HTTP_METHOD.GET });
  }

  post(options) {
    return this.request({ ...options, method: this.HTTP_METHOD.POST });
  }

  /**
   * 带弱网重试功能的Ajax方法
   */
  ajaxWithRetry(options, cacheValue, isRetryed) {
    const cacheConfig = options.cache;
    const ajaxOptions = {
      method: options.method ?? this.HTTP_METHOD.GET,
      url: options.url,
      timeout: options.timeout ?? this.$config.fetch.defaultTimeout ?? 1e4,
      data: options.params ?? options.data ?? {},
      ...options.axiosOptions,
    };

    // 自定义设置跨域鉴权开关
    if (options.withCredentials ?? false) {
      ajaxOptions.withCredentials = options.withCredentials;
    }

    // 自定义headers
    if (options.headers) {
      ajaxOptions.headers = Fetch.removeUndefined(options.headers);
    }

    // 强制GET请求不命中HTTP缓存
    // GET请求将data给params
    if (ajaxOptions.method === this.HTTP_METHOD.GET) {
      // 自动添加防缓存时间戳参数
      ajaxOptions.params = options.noTimestamp ? ajaxOptions.data : { ...ajaxOptions.data, _t: options.startTime };
      delete ajaxOptions.data;
    }

    return axios(ajaxOptions).then(response => {
      const { errorCode, errorMessage } = this.checkResponse(response, options);
      if (this.$config.fetch.maxCacheCount > 0
        && cacheConfig
        && cacheConfig.key
        && !errorCode) {
        // 添加到内存级缓存逻辑
        if (cacheConfig.online === null) {
          this.memoryCache[cacheConfig.key] = response.data;
        } else {
          // 更新/添加持久化缓存
          localForage.setItem(cacheConfig.key, {
            time: options.startTime,
            data: response.data,
          }).then(() => {
            if (!cacheValue) {
              // 没有传入旧缓存，则说明不是更新，是新添加缓存，那么需要清理旧缓存
              this.cleaupCache();
            }
          });
        }
      }
      return this.resolveFetchResponse(
        options,
        response.data,
        response.status,
        '',
        errorCode,
        errorMessage,
        this.SOURCE.AJAX,
        isRetryed,
        response,
      );
    }).catch(e => {
      const httpErrorMessage = e.message ?? '';
      // 业务异常
      if (e.response && e.response.data) {
        const { errorCode, errorMessage } = this.checkResponse(e.response, options);
        return this.resolveFetchResponse(
          options,
          e.response.data,
          e.response.status,
          httpErrorMessage,
          errorCode,
          errorMessage,
          this.SOURCE.AJAX,
          isRetryed,
          e.response,
        );
      }

      // 超时异常
      if (/TIMEOUT/i.test(httpErrorMessage)) {
        return this.resolveFetchResponse(
          options,
          {},
          0,
          httpErrorMessage,
          this.ERROR_CODE.TIMEOUT,
          this.ERROR_CODE.TIMEOUT,
          this.SOURCE.AJAX,
          isRetryed,
          null,
        );
      }

      // 其它网络错误，先走弱网重试
      const retryDelay = this.$config.fetch.retryDelay ?? 1e3;
      if (retryDelay > 0 && options.method === this.HTTP_METHOD.GET && !isRetryed) {
        // 启用了弱网重试且是get请求且还没重试过，则进行重试
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(this.ajaxWithRetry(options, cacheValue, true));
          }, retryDelay);
        });
      }

      // 如果有缓存
      if (cacheValue) {
        // 重试失败，视为断网
        if (cacheConfig.offline && options.startTime - cacheValue.time < cacheConfig.offline) {
          // 返回离线缓存
          return this.resolveFetchResponse(
            options,
            cacheValue.data,
            200,
            '',
            this.ERROR_CODE.SUCCESS,
            '',
            this.SOURCE.CACHE,
            isRetryed,
            null,
          );
        }
        // 离线缓存也过期了，则删除已过期的缓存
        localForage.removeItem(cacheConfig.key);
      }

      // 没有缓存，返回网络异常
      return this.resolveFetchResponse(
        options,
        {},
        e.response?.status ?? 0,
        httpErrorMessage,
        this.ERROR_CODE.NETWORK_ERROR,
        this.ERROR_CODE.NETWORK_ERROR,
        this.SOURCE.AJAX,
        isRetryed,
        e.response,
      );
    });
  }

  /**
   * response检查
   */
  checkResponse(response, options) {
    const result = {
      errorCode: this.ERROR_CODE.SUCCESS,
      errorMessage: '',
    };

    if (!response) {
      result.errorCode = this.ERROR_CODE.NO_RESPONSE;
      result.errorMessage = this.ERROR_CODE.NO_RESPONSE;
      return result;
    }

    // 原始返回时，不检查返回结构
    if (options.rawResponse) {
      return result;
    }

    if (isNullOrUndefined(response.data)) {
      result.errorCode = this.ERROR_CODE.NO_RESPONSE_DATA;
      result.errorMessage = this.ERROR_CODE.NO_RESPONSE_DATA;
      return result;
    }

    if (isNullOrUndefined(response.data.code)) {
      // 没有code字段
      result.errorCode = this.ERROR_CODE.SERVER_ERROR;
      result.errorMessage = this.ERROR_CODE.SERVER_ERROR;
    } else {
      // 有服务器返回的code字段
      switch (response.data.code) {
        case 404:
          result.errorCode = this.ERROR_CODE.NETWORK_ERROR;
          result.errorMessage = this.ERROR_CODE.NETWORK_ERROR;
          break;
        case 0:
        case 200:
          result.errorCode = this.ERROR_CODE.SUCCESS;
          result.errorMessage = '';
          break;
        default:
          result.errorCode = response.data.code ?? this.ERROR_CODE.BIZ_ERROR;
          result.errorMessage = response.data.msg ?? response.data.message ?? this.ERROR_CODE.BIZ_ERROR;
          break;
      }
    }

    return result;
  }

  /**
   * 响应数据结构化
   */
  resolveFetchResponse(options, data, httpCode, httpMessage,
    errorCode, errorMessage, source, retry, response) {
    const endTime = new Date().getTime();
    // 处理挂起的幂等请求
    if (options.$idem) {
      const pendings = options.$idem.pendings;
      if (pendings && pendings.length) {
        // 异步执行
        Promise.resolve().then(() => {
          while (pendings.length) {
            const pending = pendings.shift();
            const pendingResponse = {
              options: pending.options,
              data,
              httpCode,
              httpMessage,
              errorCode,
              errorMessage,
              startTime: pending.options.startTime,
              endTime,
              source: this.SOURCE.IDEM,
              retry,
            };
            pending.resolve(this.hooks.afterResponse(pendingResponse) ?? pendingResponse);
          }
        });
      }
      delete options.$idem;
      delete this.idemOptions[options.id];
    }

    // 返回响应内容
    const fetchResponse = {
      options,
      data,
      httpCode,
      httpMessage,
      errorCode,
      errorMessage,
      startTime: options.startTime,
      endTime,
      source,
      retry,
      response,
    };
    return Promise.resolve(this.hooks.afterResponse(fetchResponse) ?? fetchResponse);
  }

  /**
   * 获取默认请求参数hash（依赖参数、头、方法）
   * @param {请求配置} options
   */
  // eslint-disable-next-line class-methods-use-this
  getUniqueKey(options) {
    let params = null;
    if (options.params instanceof FormData) {
      params = {};
      for (const [key, value] of options.params) {
        params[key] = value;
      }
    } else {
      params = options.params;
    }
    const vars = Fetch.removeUndefined({ ...options.headers, ...params }, true);
    const sortedkeys = Object.keys(vars).sort();
    const kvps = [];
    for (const k of sortedkeys) {
      kvps.push(`${k}=${JSON.stringify(vars[k])}`);
    }
    return getHashString(`${this.$config.fetch.cacheKeyPrefix}-${options.method}-${kvps.join('&')}`);
  }

  /**
   * 删除指定url的接口缓存
   */
  removeCache(url) {
    const re = new RegExp(`^${url}-`, 'i');
    // 删除内存缓存
    for (const key in this.memoryCache) {
      if (re.test(key)) {
        delete this.memoryCache[key];
      }
    }

    // 删除本地缓存
    const removes = [];
    return localForage.iterate((value, key) => {
      if (re.test(key)) {
        removes.push(key);
      }
    }).then(() => Promise.all(removes.map(key => localForage.removeItem(key))));
  }

  /**
   * 批量清理缓存
   */
  cleaupCache() {
    localForage.length().then(count => {
      let surplus = count - this.$config.fetch.maxCacheCount;
      // 缓存个数超标，需要清理
      if (surplus > 0) {
        const removes = [];
        localForage.iterate((value, key) => {
          removes.push({ key, time: value.time });
        }).then(() => {
          removes.sort((a, b) => a.time - b.time);
          // 清理超出最大数量一半的那些
          surplus = parseInt(count - this.$config.fetch.maxCacheCount / 2, 10);
          for (let i = 0; i < surplus; i += 1) {
            localForage.removeItem(removes[i].key);
          }
        });
      }
    });
  }

  /**
   * Mock处理函数
   */
  mockHandle(options) {
    for (const config of this.mockConfig) {
      if (Fetch.isHitMockConfig(options, config)) {
        const mc = {};
        for (const k in config) {
          mc[k] = typeof config[k] === 'function' ? config[k](options) : config[k];
        }
        const checkResponse = this.checkResponse(mc, options);
        return this.resolveFetchResponse(
          options,
          mc.data,
          mc.httpCode ?? 200,
          mc.httpMessage ?? '',
          checkResponse.errorCode,
          checkResponse.errorMessage,
          this.SOURCE.MOCK,
          false,
          mc,
        ).then(response => {
          response.isMock = true;
          return new Promise(resolve => {
            if (Number.isInteger(mc.delay) && mc.delay > 0) {
              setTimeout(() => {
                // 重写结束时间（模拟接口延时）
                response.endTime = new Date().getTime();
                resolve(response);
              }, mc.delay);
            } else {
              resolve(response);
            }
          });
        });
      }
    }
    return null;
  }

  static isHitMockConfig(item, config) {
    if (config.disabled) {
      return false;
    }
    if (typeof config.filter === 'string') {
      return item.url.toLowerCase() === config.filter.toLowerCase();
    }
    if (config.filter instanceof RegExp) {
      return config.filter.test(item.url);
    }
    if (typeof config.filter === 'function') {
      return config.filter(item);
    }
    return false;
  }

  static removeUndefined(item, removeNull = false) {
    if (item) {
      for (const key in item) {
        if (item[key] === undefined || (removeNull && item[key] === null)) {
          delete item[key];
        }
      }
    }
    return item;
  }
};

export default Fetch;
